import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Row, Col, Card, Button, ButtonGroup, Form, FormGroup, FormText, Alert, Badge } from 'react-bootstrap';
import ReactToPrint from 'react-to-print';

import { registerRequest, USER_EMAIL_REGEX, USER_PHONE_REGEX } from '../../../User/UserActions.js';
import { USER_GROUP_DEFAULT } from '../../../User/UserGroupActions.js';
import { editAddressRequest } from '../../../Address/AddressActions.js';
import { displayErrors } from '../../../Error/ErrorActions.js';
import { registerPartnerRequest, SOCIAL_PLATFORMS } from '../../PartnerActions.js';

import withRouter from '../../../../components/Router/WithRouter.js';
import Icon from '../../../../components/Content/Icon.js';
import HtmlComponent from '../../../../components/Text/HtmlComponent.js';
import AddressEditForm from '../../../Address/components/form/AddressEditForm.js';
import InfluencerContract from '../view/InfluencerContract.js';

class InfluencerForm extends Component {
    constructor(props) {
        super(props);

        this.state = {
            step: 1,
            socialPseudo: '',
            networks: {},
			firstName: '',
			lastName: '',
			email: '',
			phone: '',
            address: null,
            cgvAccepted: false,
            isProcessing: false,
            registeredUser: null,
			errorFields: [],
        };
    }

    getActivePlatformsList = () => {
        return Object.entries(this.state.networks)
            .filter(([, net]) => net && net.active)
            .map(([platform]) => {
                const found = SOCIAL_PLATFORMS.find(p => p.key === platform);
                return found
                    ? this.props.intl.formatMessage({ id: found.labelId, defaultMessage: found.defaultMessage })
                    : platform;
            });
    };

    isValidStep1 = () => {
		if(process.env.NODE_ENV === 'development') {
			return true;
		}
        const { firstName, lastName, email, socialPseudo, networks } = this.state;
        return firstName
            && lastName
            && email
            && USER_EMAIL_REGEX.test(email)
            && socialPseudo
            && Object.values(networks).some(net => net && net.active);
    };

	handleChange = event => {
		const { name, value } = event.target;
		this.setState({ [name]: value });
	};

    handleTogglePlatform = platform => {
        this.setState(prev => ({
            networks: {
                ...prev.networks,
                [platform]: { ...(prev.networks[platform] || {}), active: !(prev.networks[platform] || {}).active },
            },
        }));
    };

    handleNetworkChange = (platform, field, value) => {
        this.setState(prev => ({
            networks: {
                ...prev.networks,
                [platform]: { ...(prev.networks[platform] || {}), [field]: value },
            },
        }));
    };

    handleAddressCapture = address => {
        if(address && typeof address === 'object' && Object.keys(address).length > 0) {
            this.setState({ address, step: 3 });
        } else {
            this.setState({ step: 1 });
        }
    };

    handleSubmitStep1 = event => {
        event.preventDefault();
        if(this.isValidStep1()) {
            this.setState({ step: 2, errorFields: [] });
        } else {
            const errorFields = [];
            if(!this.state.firstName) errorFields.push('firstName');
            if(!this.state.lastName) errorFields.push('lastName');
            if(!this.state.email || !USER_EMAIL_REGEX.test(this.state.email)) errorFields.push('email');
            if(!this.state.phone || !USER_PHONE_REGEX.test(this.state.phone)) errorFields.push('phone');
            if(!this.state.socialPseudo) errorFields.push('socialPseudo');
            this.setState({ errorFields });
            displayErrors('error', this.props.intl.formatMessage({ id: 'alertFormRequiredFieldsError' }));
        }
    };

    handleSubmitStep3 = async event => {
        event.preventDefault();

        if(!this.state.cgvAccepted) {
            displayErrors('warning', this.props.intl.formatMessage({
                id: 'influenceurCgvRequired',
                defaultMessage: 'You must accept the terms and conditions.',
            }));
            return;
        }

        this.setState({ isProcessing: true });

        try {
            const result = await this.props.dispatch(registerRequest({
                email: this.state.email,
				phone: this.state.phone,
                firstName: this.state.firstName,
                lastName: this.state.lastName,
                preferences: { language: this.props.intl.locale },
                group: USER_GROUP_DEFAULT,
            }, true, true));

            if(result && result.user) {
                await this.props.dispatch(editAddressRequest({
                    ...this.state.address,
                    user: result.user._id,
                    label: this.state.address.label || `${result.user.firstName} ${result.user.lastName}`,
                    isUsedForInvoice: true,
                }));

                await this.props.dispatch(registerPartnerRequest({
                    user: result.user._id,
                    type: 'influencer',
                    socialPseudo: this.state.socialPseudo,
                    networks: this.state.networks,
                }));

                this.setState({
                    isProcessing: false,
                    registeredUser: result.user,
                    step: 4,
                });

                displayErrors('success', this.props.intl.formatMessage({
                    id: 'influenceurRegisterSuccess',
                    defaultMessage: 'Your registration has been confirmed! You will soon receive a confirmation email.',
                }));
            } else if(result && result.error === 'UserAlreadyExists') {
                this.setState({ isProcessing: false });
                displayErrors('warning', this.props.intl.formatMessage({
                    id: 'userAccountAlreadyExistsAlert',
                    defaultMessage: 'An account already exists with this email.',
                }));
            } else {
                this.setState({ isProcessing: false });
                displayErrors('error', this.props.intl.formatMessage({
                    id: 'influenceurRegisterError',
                    defaultMessage: 'An error occurred during your registration. Please try again.',
                }));
            }
        } catch (err) {
            this.setState({ isProcessing: false });
            displayErrors('error', this.props.intl.formatMessage({
                id: 'influenceurRegisterError',
                defaultMessage: 'An error occurred during your registration. Please try again.',
            }));
        }
    };

    hasErrorField(name) {
        return this.state.errorFields && this.state.errorFields.indexOf(name) !== -1;
    }

    renderStep1() {
        const { networks } = this.state;
        return (
            <Form onSubmit={this.handleSubmitStep1}>
                <p className="text-muted mb-4">
                    <Badge bg="secondary" className="me-2 float-end">1/3</Badge>
                    <h3><FormattedMessage id="influenceurStep1" defaultMessage="Your information" /></h3>
                </p>

                <Row>
                    <Col sm="6">
                        <FormGroup className={`mb-3${this.hasErrorField('firstName') ? ' has-danger' : ''}`}>
                            <Form.Label><FormattedMessage id="userFirstName" defaultMessage="First name" /> <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                name="firstName"
                                value={this.state.firstName}
                                className={this.hasErrorField('firstName') ? 'is-invalid' : ''}
                                onChange={this.handleChange}
                            />
                        </FormGroup>
                    </Col>
                    <Col sm="6">
                        <FormGroup className={`mb-3${this.hasErrorField('lastName') ? ' has-danger' : ''}`}>
                            <Form.Label><FormattedMessage id="userLastName" defaultMessage="Last name" /> <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                name="lastName"
                                value={this.state.lastName}
                                className={this.hasErrorField('lastName') ? 'is-invalid' : ''}
                                onChange={this.handleChange}
                            />
                        </FormGroup>
                    </Col>
                </Row>

                <FormGroup className={`mb-3${this.hasErrorField('email') ? ' has-danger' : ''}`}>
                    <Form.Label><FormattedMessage id="userEmail" defaultMessage="Email" /> <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                        type="email"
                        name="email"
                        value={this.state.email}
                        className={this.hasErrorField('email') ? 'is-invalid' : ''}
                        onChange={this.handleChange}
                    />
                    {this.hasErrorField('email') && (
                        <FormText className="text-danger">
                            <FormattedMessage id="userEmailError" defaultMessage="Your email address is incorrect!" />
                        </FormText>
                    )}
                </FormGroup>

                <FormGroup className={`mb-3${this.hasErrorField('phone') ? ' has-danger' : ''}`}>
                    <Form.Label><FormattedMessage id="userPhone" defaultMessage="Phone" /> <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                        type="phone"
                        name="phone"
                        value={this.state.phone}
                        className={this.hasErrorField('phone') ? 'is-invalid' : ''}
                        onChange={this.handleChange}
                    />
                    {this.hasErrorField('phone') && (
                        <FormText className="text-danger">
                            <FormattedMessage id="userPhoneError" defaultMessage="Your phone number is incorrect!" />
                        </FormText>
                    )}
                </FormGroup>

                <FormGroup className={`mb-3${this.hasErrorField('socialPseudo') ? ' has-danger' : ''}`}>
                    <Form.Label>
                        <FormattedMessage id="influenceurSocialPseudo" defaultMessage="Social media handle(s)" /> <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                        type="text"
                        name="socialPseudo"
                        value={this.state.socialPseudo}
                        className={this.hasErrorField('socialPseudo') ? 'is-invalid' : ''}
                        onChange={this.handleChange}
                        placeholder="@my_pseudo"
                    />
                </FormGroup>

                <FormGroup className="mb-4">
                    <Form.Label>
                        <FormattedMessage id="influenceurSocialPlatforms" defaultMessage="Active platforms" /> <span className="text-danger">*</span>
                    </Form.Label>
                    {SOCIAL_PLATFORMS.map(({ key, labelId, defaultMessage }) => {
                        const net = networks[key] || {};
                        return (
                            <div key={key} className={`border rounded p-2 mb-2${net.active ? ' border-success' : ''}`}>
                                <Row className="align-items-center g-2">
                                    <Col xs="auto">
                                        <Form.Check
                                            type="checkbox"
                                            id={`platform-${key}`}
                                            label={<strong><FormattedMessage id={labelId} defaultMessage={defaultMessage} /></strong>}
                                            checked={!!net.active}
                                            onChange={() => this.handleTogglePlatform(key)}
                                        />
                                    </Col>
                                    {net.active && (
                                        <>
                                            <Col>
                                                <Form.Control
                                                    type="text"
                                                    size="sm"
                                                    value={net.url || ''}
                                                    onChange={e => this.handleNetworkChange(key, 'url', e.target.value)}
                                                    placeholder={this.props.intl.formatMessage({ id: 'influenceurSocialUrl', defaultMessage: 'URL du profil' })}
                                                />
                                            </Col>
                                            <Col xs="4" sm="3">
                                                <Form.Control
                                                    type="number"
                                                    size="sm"
                                                    min="0"
                                                    value={net.audience || ''}
                                                    onChange={e => this.handleNetworkChange(key, 'audience', parseInt(e.target.value, 10) || 0)}
                                                    placeholder={this.props.intl.formatMessage({ id: 'influenceurSocialAudience', defaultMessage: 'Abonnés' })}
                                                />
                                            </Col>
                                        </>
                                    )}
                                </Row>
                            </div>
                        );
                    })}
                    {!Object.values(networks).some(net => net && net.active) && this.state.errorFields.length > 0 && (
                        <FormText className="text-danger">
                            <FormattedMessage id="influenceurSocialRequired" defaultMessage="Please select at least one platform." />
                        </FormText>
                    )}
                </FormGroup>

                <div className="text-end">
                    <Button variant="success" type="submit">
                        <FormattedMessage id="next" defaultMessage="Next" /> <Icon icon="chevron-right" />
                    </Button>
                </div>
            </Form>
        );
    }

    renderStep2() {
        return (
            <div>
                <p className="text-muted mb-4">
                    <Badge bg="secondary" className="me-2 float-end">2/3</Badge>
                    <h3><FormattedMessage id="influenceurStep2" defaultMessage="Your postal address" /></h3>
                </p>
                <AddressEditForm
                    address={null}
                    user={null}
					hidePhone
                    closeForm={this.handleAddressCapture}
                    skipSave
					submitLabel={<FormattedMessage id="next" defaultMessage="Next" />}
                />
            </div>
        );
    }

    renderStep3() {
        const { address, isProcessing } = this.state;
        const platforms = this.getActivePlatformsList();
        return (
            <Form onSubmit={this.handleSubmitStep3}>
                <p className="text-muted mb-4">
                    <Badge bg="secondary" className="me-2 float-end">3/3</Badge>
                    <h3><FormattedMessage id="influenceurStep3" defaultMessage="Confirmation" /></h3>
                </p>

                <Alert variant="light" className="border mb-4">
                    <Row>
                        <Col sm="6">
                            <div className="mb-2">
                                <strong><FormattedMessage id="userFirstName" defaultMessage="First name" /> / <FormattedMessage id="userLastName" defaultMessage="Last name" /></strong><br />
                                {this.state.firstName} {this.state.lastName}
                            </div>
                            <div className="mb-2">
                                <strong><FormattedMessage id="userEmail" defaultMessage="Email" /></strong><br />
                                {this.state.email}
                            </div>
                            <div className="mb-2">
                                <strong><FormattedMessage id="influenceurSocialPseudo" defaultMessage="Social media handle(s)" /></strong><br />
                                {this.state.socialPseudo}
                            </div>
                            <div>
                                <strong><FormattedMessage id="influenceurSocialPlatforms" defaultMessage="Active platforms" /></strong><br />
                                {platforms.join(', ')}
                            </div>
                        </Col>
                        <Col sm="6">
                            <strong><FormattedMessage id="addressShipping" defaultMessage="Postal address" /></strong><br />
                            {address && (
                                <Fragment>
                                    {address.name && <div>{address.name}</div>}
                                    <div>{address.street1}</div>
                                    {address.street2 && <div>{address.street2}</div>}
                                    <div>{address.postalCode} {address.city}</div>
                                    {address.country && <div>{address.country}</div>}
                                    {address.phone && <div>{address.phone}</div>}
                                </Fragment>
                            )}
                        </Col>
                    </Row>
                </Alert>

                <FormGroup className="mb-4">
                    <Form.Check
                        type="checkbox"
                        id="cgv-accept"
                        label={
                            <FormattedMessage
                                id="influenceurCgvAccept"
                                defaultMessage="I accept the shop's terms and conditions"
                            />
                        }
                        checked={this.state.cgvAccepted}
                        onChange={e => this.setState({ cgvAccepted: e.target.checked })}
                    />
                </FormGroup>

                <ButtonGroup className='w-100'>
                    <Button variant="light" onClick={() => this.setState({ step: 2 })}>
                        <Icon icon="chevron-left" /> <FormattedMessage id="back" defaultMessage="Back" />
                    </Button>
                    <Button variant="success" type="submit" disabled={!this.state.cgvAccepted || isProcessing}>
                        {isProcessing
                            ? <Fragment><Icon icon="spinner" spin /> <FormattedMessage id="saving" defaultMessage="Saving..." /></Fragment>
                            : <Fragment><Icon icon="user-add" /> <FormattedMessage id="userRegister" defaultMessage="Create my account" /></Fragment>}
                    </Button>
                </ButtonGroup>
            </Form>
        );
    }

    renderStep4() {
        const { registeredUser, address } = this.state;
        const { successContent } = this.props;
        return (
            <div>
				<Alert variant="success" className="mb-2">
					<Icon icon="check-o" /> <strong><FormattedMessage id="influenceurSuccessTitle" defaultMessage="Registration confirmed!" /></strong>
					<br />
					<FormattedMessage id="influenceurRegisterSuccess" defaultMessage="Your registration has been confirmed! You will soon receive a confirmation email." />
				</Alert>
				{successContent && <HtmlComponent text={successContent} className="mb-3" />}

                <div className="mt-3">
                    <ReactToPrint
                        trigger={() => (
                            <Button variant="outline-dark" className='w-100'>
                                <Icon icon="printer" /> <FormattedMessage id="influenceurContractPrint" defaultMessage="Print the partnership contract" />
                            </Button>
                        )}
                        content={() => this.contractPrintRef}
                    />
                </div>

                {registeredUser && address && (
					<div className="d-none">
						<div ref={elt => { this.contractPrintRef = elt; }}>
							<InfluencerContract
								user={registeredUser}
								address={address}
								socialPseudo={this.state.socialPseudo}
								activePlatforms={this.getActivePlatformsList()}
							/>
						</div>
					</div>
				)}
            </div>
        );
    }

    render() {
        const { step } = this.state;

        return (
			<Fragment>
                {this.props.introContent && step < 2 && (
					<Card body className="mb-4 shadow">
						<HtmlComponent text={this.props.introContent} />
					</Card>
				)}
				<Card className="mb-5 shadow">
					<Card.Body className="p-4">
						{step === 1 && this.renderStep1()}
						{step === 2 && this.renderStep2()}
						{step === 3 && this.renderStep3()}
						{step === 4 && this.renderStep4()}
					</Card.Body>
				</Card>
			</Fragment>
        );
    }
}

function mapStateToProps(store) {
    return {
        intl: store.intl,
    };
}

InfluencerForm.defaultProps = {
    introContent: null,
    successContent: null,
};

InfluencerForm.propTypes = {
    dispatch: PropTypes.func.isRequired,
    intl: PropTypes.object.isRequired,
    introContent: PropTypes.string,
    successContent: PropTypes.string,
};

export default withRouter(connect(mapStateToProps)(injectIntl(InfluencerForm)));
